import React from 'react';
import { SiReact, SiNodedotjs, SiMongodb, SiNextdotjs, SiFirebase } from 'react-icons/si';
import zapxImg from '../../assets/images/zapx-phone.png';
import quickchatImg from '../../assets/images/quickchat-phone.png';
import handsImg from '../../assets/images/hand_connect.jpg';
import { Container, Eyebrow, PillButton, navyIn, NAVY_SECTION } from './ui';

const blueCard =
  'bg-[radial-gradient(120%_90%_at_25%_0%,#3b5bfd_0%,#6f86ff_40%,#c9d2ff_75%,#eef1fb_100%)] text-white';
const lightCard = 'bg-[#f4f6fa] text-[#111]';

const CardHead = ({ title, desc, dark }) => (
  <div className="relative z-10 p-5 sm:p-6">
    <h3 className={`text-2xl sm:text-[26px] font-medium tracking-[-0.03em] ${dark ? 'text-white' : 'text-[#111]'}`}>{title}</h3>
    <p className={`mt-3 text-sm leading-relaxed max-w-[240px] ${dark ? 'text-white/85' : 'text-[#111]/70'}`}>{desc}</p>
  </div>
);

const stackPills = [
  { label: 'React', className: 'left-[16%] bottom-[34%]' },
  { label: 'Next.js', className: 'left-[42%] bottom-[44%] rotate-[22deg]' },
  { label: 'Node.js', className: 'left-[36%] bottom-[24%]' },
  { label: 'REST APIs', className: 'left-[6%] bottom-[14%]' },
  { label: 'MongoDB', className: 'right-[6%] bottom-[16%]' },
  { label: 'Express', className: 'left-[7%] bott om-[3%]' },
  { label: 'Tailwind', className: 'right-[6%] bottom-[3%]' },
];

const orbit = [
  { Icon: SiReact, color: '#61dafb', angle: 168 },
  { Icon: SiNodedotjs, color: '#5fa04e', angle: 128 },
  { Icon: SiNextdotjs, color: '#ffffff', angle: 90 },
  { Icon: SiMongodb, color: '#47a248', angle: 52 },
  { Icon: SiFirebase, color: '#ffca28', angle: 14 },
];

const Services = () => (
  <section id="services" className="relative">
    <div className={NAVY_SECTION} style={navyIn}>
      <Container>
        <div className="text-center">
          <Eyebrow dark>Core Services</Eyebrow>
          <h2 className="mt-3 text-[34px] sm:text-[44px] lg:text-[48px] font-medium leading-[1.05] tracking-[-0.04em] text-white">
            Engineering Solutions Built
            <br className="hidden sm:block" /> For Growth
          </h2>
        </div>

        <div className="mt-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Web development */}
            <div className={`relative rounded-2xl overflow-hidden min-h-[300px] ${blueCard}`}>
              <CardHead dark title="Web Development" desc="Fast, scalable web apps and platforms built with a modern full-stack toolkit." />
              {stackPills.map((p) => (
                <span
                  key={p.label}
                  className={`absolute bg-white/85 backdrop-blur text-[#111]/75 text-xs rounded-full px-4 py-1.5 shadow-sm ${p.className}`}
                >
                  {p.label}
                </span>
              ))}
            </div>

            {/* Mobile apps */}
            <div className={`relative rounded-2xl overflow-hidden min-h-[300px] flex flex-col ${lightCard}`}>
              <CardHead title="Mobile Apps" desc="Cross-platform iOS and Android apps with smooth, native-feeling UX." />
              <div className="mt-auto mx-5 mb-5 h-[130px] rounded-xl overflow-hidden">
                <img
                  src={process.env.PUBLIC_URL + '/assets/img/services/mobileapp.jpg'}
                  alt="Mobile and web app screens on devices"
                  loading="lazy"
                  className="w-full h-full object-cover object-[50%_40%]"
                />
              </div>
            </div>

            {/* CTA card spans two rows */}
            <div className={`relative rounded-2xl overflow-hidden p-4 sm:p-5 flex flex-col md:col-span-2 lg:col-span-1 lg:row-span-2 ${lightCard}`}>
              {/* Reaching hands on a white backdrop that matches the photo's own background */}
              <div className="relative h-[240px] lg:h-auto lg:flex-1 lg:min-h-[260px] rounded-xl overflow-hidden bg-white ring-1 ring-inset ring-black/5">
                <img
                  src={handsImg}
                  alt="Two hands reaching toward each other"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
              </div>
              <div className="h-px bg-linear-to-r from-transparent via-[#3b5bfd]/50 to-transparent my-5" />
              <span className="self-start bg-[#e6e9f0] text-[#111]/80 text-sm rounded-full px-4 py-1.5">
                Trusted By 43+ Clients
              </span>
              <h3 className="mt-5 text-2xl sm:text-[26px] font-medium tracking-[-0.03em] text-[#111]">Let's Build Something</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#111]/70 max-w-[260px]">
                Turn your idea into a product that is reliable, scalable, and built to last.
              </p>
              <PillButton to="/contact" className="mt-6 self-start">
                Start A Project
              </PillButton>
            </div>

            {/* Software / ERP */}
            <div className={`relative rounded-2xl overflow-hidden min-h-[300px] ${blueCard}`}>
              <CardHead dark title="Software & ERP" desc="Custom business software, dashboards, and automation that save hours every week." />
              <div className="absolute left-0 right-0 bottom-0 flex gap-3 pl-0 -ml-8">
                {[zapxImg, quickchatImg, zapxImg].map((src, i) => (
                  <div
                    key={i}
                    className="w-[120px] h-[96px] shrink-0 rounded-xl overflow-hidden bg-linear-to-b from-[#eef1fb] to-[#c9d2ff] border border-white/60 shadow-md flex justify-center pt-3"
                  >
                    <img src={src} alt="" loading="lazy" className="w-[78px] h-auto self-start drop-shadow-md" />
                  </div>
                ))}
              </div>
            </div>

            {/* Tech stack orbit */}
            <div className={`relative rounded-2xl overflow-hidden min-h-[300px] ${lightCard}`}>
              <CardHead title="Integrations & AI" desc="APIs, payments, cloud, and AI features wired cleanly into your product." />
              <div className="absolute left-1/2 -bottom-[150px] -translate-x-1/2 w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle,#3b5bfd_0%,#7d93ff_30%,#c9d2ff_55%,transparent_70%)] opacity-80" />
              <div className="absolute left-1/2 -bottom-[150px] -translate-x-1/2 w-[300px] h-[300px] rounded-full border border-dashed border-[#3b5bfd]/60" />
              {orbit.map(({ Icon, color, angle }) => {
                const rad = (angle * Math.PI) / 180;
                return (
                  <span
                    key={angle}
                    className="absolute w-10 h-10 -ml-5 -mb-5 rounded-full bg-[#111] flex items-center justify-center shadow-lg"
                    style={{ left: `calc(50% + ${Math.cos(rad) * 150}px)`, bottom: `${Math.sin(rad) * 150}px` }}
                  >
                    <Icon className="w-5 h-5" style={{ color }} />
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </Container>
    </div>
  </section>
);

export default Services;
